
export default function ArchitectureSection() {
  const layers = [
    {
      title: 'Presentation Layer',
      subtitle: 'Gebruikersinterface',
      description: 'Gebruiksvriendelijk portaal & app die relevante data toont aan de juiste actor: Controller, Chauffeur, Reiziger, Planner, Directie.',
      icon: 'ri-window-line',
      color: 'from-blue-500 to-blue-600',
      features: ['Responsive Design', 'Rolgebaseerde Toegang', 'Realtime Updates', 'Intuïtieve Interface']
    },
    {
      title: 'Business Logic Layer',
      subtitle: 'Intelligente Tussenlaag',
      description: 'De intelligente tussenlaag die data controleert, analyseert en meldingen/instructies geeft op basis van vooraf gedefinieerde regels.',
      icon: 'ri-brain-line',
      color: 'from-teal-500 to-teal-600',
      features: ['Automatische Validatie', 'Slimme Routing', 'Event Processing', 'Notificatie Engine']
    },
    {
      title: 'Data Access Layer',
      subtitle: 'Data Fundament',
      description: 'Continu gevuld met realtime data: reizigersdata, planning, uitvoering, monitoring en historische informatie voor analyse.',
      icon: 'ri-database-2-line',
      color: 'from-green-500 to-green-600',
      features: ['Realtime Synchronisatie', 'Data Integriteit', 'Veilige Opslag', 'API Connectiviteit']
    }
  ];

  return (
    <section id="architectuur" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Modulaire Bouw: Drie Lagen van Intelligentie
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Een robuuste, schaalbare architectuur die technische betrouwbaarheid garandeert
          </p>
        </div>

        <div className="space-y-6 sm:space-y-8">
          {layers.map((layer, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-6 sm:p-8 border border-gray-200"
            >
              <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-2">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${layer.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${layer.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">
                        Laag {index + 1}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                        {layer.title}
                      </h3>
                      <div className="text-teal-600 font-medium text-sm sm:text-base">
                        {layer.subtitle}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6 text-sm sm:text-base">
                    {layer.description}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6">
                  <h4 className="font-bold text-gray-900 mb-4 text-sm sm:text-base">Kernfunctionaliteiten</h4>
                  <ul className="space-y-3">
                    {layer.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Architecture Diagram */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 lg:p-12">
          <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6 sm:mb-8">
            Data Flow Architectuur
          </h3>
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0 lg:space-x-8">
            {/* External Systems */}
            <div className="flex flex-col items-center space-y-3 sm:space-y-4">
              <div className="text-gray-400 text-xs sm:text-sm font-semibold mb-2">Externe Systemen</div>
              {['Vervoerders', 'GPS Tracking', 'Planning Tools'].map((system, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-white text-xs sm:text-sm border border-white/20 whitespace-nowrap">
                  {system}
                </div>
              ))}
            </div>

            {/* Arrow */}
            <div className="hidden lg:block">
              <i className="ri-arrow-right-line text-3xl sm:text-4xl text-teal-400"></i>
            </div>
            <div className="lg:hidden">
              <i className="ri-arrow-down-line text-3xl sm:text-4xl text-teal-400"></i>
            </div>

            {/* EasyTravel Core */}
            <div className="flex-1 max-w-xs sm:max-w-md">
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 sm:p-6 text-center">
                <div className="text-lg sm:text-2xl font-bold text-white mb-2">EasyTravel Platform</div>
                <div className="text-teal-100 text-xs sm:text-sm">3-Laags Architectuur</div>
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:block">
              <i className="ri-arrow-right-line text-3xl sm:text-4xl text-teal-400"></i>
            </div>
            <div className="lg:hidden">
              <i className="ri-arrow-down-line text-3xl sm:text-4xl text-teal-400"></i>
            </div>

            {/* Users */}
            <div className="flex flex-col items-center space-y-3 sm:space-y-4">
              <div className="text-gray-400 text-xs sm:text-sm font-semibold mb-2">Gebruikers</div>
              {['Controller', 'Chauffeur', 'Reiziger', 'Directie'].map((user, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-white text-xs sm:text-sm border border-white/20 whitespace-nowrap">
                  {user}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
